/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';

import ErrorImage from '../../assets/warning.png';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';

import { messages } from './messages';

const styles = cssMap({
	modalContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		borderRadius: token('radius.large'),
		marginTop: token('space.600'),
		marginLeft: token('space.600'),
		marginRight: token('space.600'),
		marginBottom: token('space.600'),
		textAlign: 'center',
		flex: 1,
	},
	iconWrapper: {
		marginBottom: token('space.200'),
		marginTop: token('space.400'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	heading: {
		marginBottom: token('space.200'),
	},
	description: {
		marginBottom: token('space.600'),
		maxWidth: '400px',
	},
	buttons: {
		display: 'flex',
		gap: token('space.050'),
	},
});

type ErrorScreenProps = {
	onClose: () => void;
};

export const ErrorScreen: (props: ErrorScreenProps) => JSX.Element = ({ onClose }: ErrorScreenProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { sourceComponent, sourceContext } = useModalContext();

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalErrorScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	const handleContactSupportClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalErrorScreenContactSupport',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	};
	return (
		<ScreenLayout testId="error-screen">
			<ScreenLayout.Content>
				<Box as="div" xcss={styles.modalContent}>
					<Box xcss={styles.iconWrapper}>
						<Image
							src={ErrorImage}
							width={150}
							height={150}
							testId="error-icon"
							alt={intl.formatMessage(messages.errorIconAlt)}
						/>
					</Box>

					<Box xcss={styles.heading}>
						<Heading size="medium">
							<FormattedMessage {...messages.heading} />
						</Heading>
					</Box>

					<Box xcss={styles.description}>
						<Text size="medium" weight="regular" color="color.text.subtlest">
							<FormattedMessage {...messages.description} />
						</Text>
					</Box>

					<Box xcss={styles.buttons}>
						<Button appearance="subtle" onClick={onClose}>
							<FormattedMessage {...messages.closeButton} />
						</Button>
						<LinkButton
							appearance="primary"
							href="https://support.atlassian.com/contact/#/"
							target="_blank"
							onClick={handleContactSupportClick}
						>
							<FormattedMessage {...messages.contactSupportButton} />
						</LinkButton>
					</Box>
				</Box>
			</ScreenLayout.Content>
		</ScreenLayout>
	);
};
